import React, { useState } from 'react';
import { Download, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import Sidebar, { useSidebar } from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import TopNavbar from '../../components/ui/TopNavbar';

const FinancialStatements = () => {
  const { isCollapsed } = useSidebar();
  const [activeView, setActiveView] = useState('profit-loss');
  const [selectedPeriod, setSelectedPeriod] = useState('2024-12');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);

  // Current USD to AUD exchange rate (from Exchange Rates page)
  const USD_TO_AUD_RATE = 1.5564;
  
  // GST rate for Australia (10%)
  const GST_RATE = 0.10;

  // Helper function to convert USD to AUD
  const convertUSDtoAUD = (usdAmount) => {
    return usdAmount * USD_TO_AUD_RATE;
  };

  // Helper function to calculate GST
  const calculateGST = (amount) => {
    return amount * GST_RATE;
  };

  // Original USD amounts - these are the source of truth
  const originalUSDData = {
    revenue: {
      salesRevenue: 1606000, // Original USD amount
      serviceRevenue: 289000, // Original USD amount
      otherIncome: 48200, // Original USD amount
    },
    expenses: {
      costOfGoodsSold: 771000, // Original USD amount
      operatingExpenses: 417500, // Original USD amount
      salariesWages: 257000, // Original USD amount (GST-free)
      rentUtilities: 77100, // Original USD amount
      marketing: 51400, // Original USD amount
      depreciation: 32100, // Original USD amount (GST-free)
    },
    assets: {
      currentAssets: {
        cash: 160600,
        accountsReceivable: 257000,
        gstReceivable: 28900,
        inventory: 128500,
      },
      fixedAssets: {
        propertyEquipment: 963800,
        accumulatedDepreciation: -128500,
      }
    },
    liabilities: {
      currentLiabilities: {
        accountsPayable: 115600,
        accruedExpenses: 89900,
        gstPayable: 33700,
      },
      longTermDebt: 481800,
    },
    equity: {
      ownerEquity: 642500,
      retainedEarnings: 51400,
    }
  };

  // Mock P&L data with ACTUAL USD TO AUD CONVERSION + GST
  const profitLossData = {
    revenue: {
      salesRevenueUSD: originalUSDData?.revenue?.salesRevenue,
      salesRevenue: convertUSDtoAUD(originalUSDData?.revenue?.salesRevenue),
      salesGST: calculateGST(convertUSDtoAUD(originalUSDData?.revenue?.salesRevenue)),
      serviceRevenueUSD: originalUSDData?.revenue?.serviceRevenue,
      serviceRevenue: convertUSDtoAUD(originalUSDData?.revenue?.serviceRevenue),
      serviceGST: calculateGST(convertUSDtoAUD(originalUSDData?.revenue?.serviceRevenue)),
      otherIncomeUSD: originalUSDData?.revenue?.otherIncome,
      otherIncome: convertUSDtoAUD(originalUSDData?.revenue?.otherIncome),
      otherIncomeGST: calculateGST(convertUSDtoAUD(originalUSDData?.revenue?.otherIncome)),
      get subtotal() {
        return this.salesRevenue + this.serviceRevenue + this.otherIncome;
      },
      get totalGST() {
        return this.salesGST + this.serviceGST + this.otherIncomeGST;
      },
      get total() {
        return this.subtotal + this.totalGST;
      }
    },
    expenses: {
      costOfGoodsSoldUSD: originalUSDData?.expenses?.costOfGoodsSold,
      costOfGoodsSold: convertUSDtoAUD(originalUSDData?.expenses?.costOfGoodsSold),
      cogsGST: calculateGST(convertUSDtoAUD(originalUSDData?.expenses?.costOfGoodsSold)),
      operatingExpensesUSD: originalUSDData?.expenses?.operatingExpenses,
      operatingExpenses: convertUSDtoAUD(originalUSDData?.expenses?.operatingExpenses),
      operatingGST: calculateGST(convertUSDtoAUD(originalUSDData?.expenses?.operatingExpenses)),
      salariesWagesUSD: originalUSDData?.expenses?.salariesWages,
      salariesWages: convertUSDtoAUD(originalUSDData?.expenses?.salariesWages),
      rentUtilitiesUSD: originalUSDData?.expenses?.rentUtilities,
      rentUtilities: convertUSDtoAUD(originalUSDData?.expenses?.rentUtilities),
      rentUtilitiesGST: calculateGST(convertUSDtoAUD(originalUSDData?.expenses?.rentUtilities)),
      marketingUSD: originalUSDData?.expenses?.marketing,
      marketing: convertUSDtoAUD(originalUSDData?.expenses?.marketing),
      marketingGST: calculateGST(convertUSDtoAUD(originalUSDData?.expenses?.marketing)),
      depreciationUSD: originalUSDData?.expenses?.depreciation,
      depreciation: convertUSDtoAUD(originalUSDData?.expenses?.depreciation),
      get subtotal() {
        return this.costOfGoodsSold + this.operatingExpenses + this.salariesWages + 
               this.rentUtilities + this.marketing + this.depreciation;
      },
      get totalGST() {
        return this.cogsGST + this.operatingGST + this.rentUtilitiesGST + this.marketingGST;
      },
      get total() {
        return this.subtotal + this.totalGST;
      }
    },
    get netIncomeBeforeGST() {
      return this.revenue?.subtotal - this.expenses?.subtotal;
    },
    get netGSTPayable() {
      return this.revenue?.totalGST - this.expenses?.totalGST;
    },
    get netIncome() {
      return this.netIncomeBeforeGST - this.netGSTPayable;
    }
  };

  // Mock Balance Sheet data with ACTUAL USD TO AUD CONVERSION + GST
  const balanceSheetData = {
    assets: {
      currentAssets: {
        cashUSD: originalUSDData?.assets?.currentAssets?.cash,
        cash: convertUSDtoAUD(originalUSDData?.assets?.currentAssets?.cash),
        accountsReceivableUSD: originalUSDData?.assets?.currentAssets?.accountsReceivable,
        accountsReceivable: convertUSDtoAUD(originalUSDData?.assets?.currentAssets?.accountsReceivable),
        gstReceivableUSD: originalUSDData?.assets?.currentAssets?.gstReceivable,
        gstReceivable: convertUSDtoAUD(originalUSDData?.assets?.currentAssets?.gstReceivable),
        inventoryUSD: originalUSDData?.assets?.currentAssets?.inventory,
        inventory: convertUSDtoAUD(originalUSDData?.assets?.currentAssets?.inventory),
        get total() {
          return this.cash + this.accountsReceivable + this.gstReceivable + this.inventory;
        }
      },
      fixedAssets: {
        propertyEquipmentUSD: originalUSDData?.assets?.fixedAssets?.propertyEquipment,
        propertyEquipment: convertUSDtoAUD(originalUSDData?.assets?.fixedAssets?.propertyEquipment),
        accumulatedDepreciationUSD: originalUSDData?.assets?.fixedAssets?.accumulatedDepreciation,
        accumulatedDepreciation: convertUSDtoAUD(originalUSDData?.assets?.fixedAssets?.accumulatedDepreciation),
        get total() {
          return this.propertyEquipment + this.accumulatedDepreciation;
        }
      },
      get totalAssets() {
        return this.currentAssets?.total + this.fixedAssets?.total;
      }
    },
    liabilities: {
      currentLiabilities: {
        accountsPayableUSD: originalUSDData?.liabilities?.currentLiabilities?.accountsPayable,
        accountsPayable: convertUSDtoAUD(originalUSDData?.liabilities?.currentLiabilities?.accountsPayable),
        accruedExpensesUSD: originalUSDData?.liabilities?.currentLiabilities?.accruedExpenses,
        accruedExpenses: convertUSDtoAUD(originalUSDData?.liabilities?.currentLiabilities?.accruedExpenses),
        gstPayableUSD: originalUSDData?.liabilities?.currentLiabilities?.gstPayable,
        gstPayable: convertUSDtoAUD(originalUSDData?.liabilities?.currentLiabilities?.gstPayable),
        get total() {
          return this.accountsPayable + this.accruedExpenses + this.gstPayable;
        }
      },
      longTermDebtUSD: originalUSDData?.liabilities?.longTermDebt,
      longTermDebt: convertUSDtoAUD(originalUSDData?.liabilities?.longTermDebt),
      get totalLiabilities() {
        return this.currentLiabilities?.total + this.longTermDebt;
      }
    },
    equity: {
      ownerEquityUSD: originalUSDData?.equity?.ownerEquity,
      ownerEquity: convertUSDtoAUD(originalUSDData?.equity?.ownerEquity),
      retainedEarningsUSD: originalUSDData?.equity?.retainedEarnings,
      retainedEarnings: convertUSDtoAUD(originalUSDData?.equity?.retainedEarnings),
      get totalEquity() {
        return this.ownerEquity + this.retainedEarnings;
      }
    }
  };

  const formatCurrency = (value) => {
    return `A$${value?.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatUSDCurrency = (value) => {
    return `$${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getPercentage = (value, total) => {
    return ((value / total) * 100)?.toFixed(1);
  };

  const handleGenerateBalanceSheet = () => {
    setIsGenerating(true);
    console.log('Generating balance sheet');
    // TODO: Implement generation
    setTimeout(() => {
      setIsGenerating(false);
      alert('✅ Balance Sheet generated!');
    }, 2000);
  };

  const handleGenerateIncomeStatement = () => {
    setIsGenerating(true);
    console.log('Generating income statement');
    // TODO: Implement generation
    setTimeout(() => {
      setIsGenerating(false);
      alert('✅ Income Statement generated!');
    }, 2000);
  };

  const handleGenerateCashFlow = () => {
    setIsGenerating(true);
    console.log('Generating cash flow statement');
    // TODO: Implement generation
    setTimeout(() => {
      setIsGenerating(false);
      alert('✅ Cash Flow Statement generated!');
    }, 2000);
  };

  const handleExportStatement = (type, format) => {
    console.log('Exporting statement:', type, format);
    // TODO: Implement export
    alert(`✅ Exporting ${type} as ${format}...`);
  };

  const handleCompareStatements = () => {
    setShowComparisonModal(true);
  };

  const handleRunComparison = (comparisonData) => {
    console.log('Running comparison:', comparisonData);
    // TODO: Implement comparison
    alert('✅ Statement comparison completed!');
    setShowComparisonModal(false);
  };

  const handleAdjustStatement = (statementId) => {
    console.log('Adjusting statement:', statementId);
    setShowAdjustmentModal(true);
    // TODO: Load adjustment interface
  };

  const handleSaveAdjustment = (adjustmentData) => {
    console.log('Saving adjustment:', adjustmentData);
    // TODO: Implement adjustment save
    alert('✅ Statement adjusted successfully!');
    setShowAdjustmentModal(false);
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-6 mt-[65px]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Financial Statements</h1>
              <p className="text-gray-600 mt-1">View profit & loss and balance sheet reports (USD converted to AUD including GST)</p>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                  Exchange Rate: 1 USD = {USD_TO_AUD_RATE} AUD
                </span>
                <span className="text-gray-500">• All amounts converted from USD</span>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export PDF
              </Button>
              <Button variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export Excel
              </Button>
            </div>
          </div>

          {/* Statement Type & Period Selection */}
          <div className="flex gap-4">
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setActiveView('profit-loss')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'profit-loss' ?'bg-white text-blue-600 shadow' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Profit & Loss
              </button>
              <button
                onClick={() => setActiveView('balance-sheet')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeView === 'balance-sheet' ?'bg-white text-blue-600 shadow' :'text-gray-600 hover:text-gray-900'
                }`}
              >
                Balance Sheet
              </button>
            </div>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="2024-12">December 2024</option>
              <option value="2024-11">November 2024</option>
              <option value="2024-10">October 2024</option>
              <option value="2024-q4">Q4 2024</option>
              <option value="2024">Full Year 2024</option>
            </select>
          </div>
        </div>

        {/* Statement Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          {activeView === 'profit-loss' ? (
            /* Profit & Loss Statement */
            (<div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">Profit & Loss Statement (AUD incl. GST)</h2>
                  <span className="text-sm text-gray-600">
                    Period: {new Date(selectedPeriod + '-01')?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Revenue Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Revenue
                  </h3>
                  <div className="space-y-2 ml-7">
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Sales Revenue (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.revenue?.salesRevenueUSD)} USD)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.revenue?.salesRevenue)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on Sales</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.revenue?.salesGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Service Revenue (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.revenue?.serviceRevenueUSD)} USD)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.revenue?.serviceRevenue)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on Services</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.revenue?.serviceGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Other Income (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.revenue?.otherIncomeUSD)} USD)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.revenue?.otherIncome)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on Other Income</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.revenue?.otherIncomeGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-t border-gray-200">
                      <span className="text-gray-700 font-medium">Revenue before GST</span>
                      <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.revenue?.subtotal)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700 font-medium">Total GST on Revenue</span>
                      <span className="font-medium text-blue-600">{formatCurrency(profitLossData?.revenue?.totalGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-t-2 border-gray-300 font-semibold">
                      <span className="text-gray-900">Total Revenue (incl. GST)</span>
                      <span className="text-green-600 text-lg">{formatCurrency(profitLossData?.revenue?.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Expenses Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-red-600" />
                    Expenses
                  </h3>
                  <div className="space-y-2 ml-7">
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Cost of Goods Sold (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.expenses?.costOfGoodsSoldUSD)} USD)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.expenses?.costOfGoodsSold)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on COGS</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.expenses?.cogsGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Operating Expenses (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.expenses?.operatingExpensesUSD)} USD)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.expenses?.operatingExpenses)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on Operating Expenses</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.expenses?.operatingGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <div>
                        <span className="text-gray-600 text-sm">• Salaries and Wages (GST-free)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.expenses?.salariesWagesUSD)} USD)</span>
                      </div>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.expenses?.salariesWages)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Rent and Utilities (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.expenses?.rentUtilitiesUSD)} USD)</span>
                      </div>
                      <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.expenses?.rentUtilities)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on Rent/Utilities</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.expenses?.rentUtilitiesGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Marketing (excl. GST)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.expenses?.marketingUSD)} USD)</span>
                      </div>
                      <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.expenses?.marketing)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 ml-4">
                      <span className="text-gray-600 text-sm">• GST on Marketing</span>
                      <span className="text-sm text-gray-900">{formatCurrency(profitLossData?.expenses?.marketingGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <div>
                        <span className="text-gray-700">Depreciation (GST-free)</span>
                        <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(profitLossData?.expenses?.depreciationUSD)} USD)</span>
                      </div>
                      <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.expenses?.depreciation)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-t border-gray-200">
                      <span className="text-gray-700 font-medium">Expenses before GST</span>
                      <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.expenses?.subtotal)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700 font-medium">Total GST on Expenses (claimable)</span>
                      <span className="font-medium text-green-600">{formatCurrency(profitLossData?.expenses?.totalGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-t-2 border-gray-300 font-semibold">
                      <span className="text-gray-900">Total Expenses (incl. GST)</span>
                      <span className="text-red-600 text-lg">{formatCurrency(profitLossData?.expenses?.total)}</span>
                    </div>
                  </div>
                </div>

                {/* GST Summary */}
                <div className="pt-4 border-t-2 border-gray-400 bg-blue-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">GST Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">GST Collected on Revenue</span>
                      <span className="font-medium text-gray-900">{formatCurrency(profitLossData?.revenue?.totalGST)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">GST Paid on Expenses (claimable)</span>
                      <span className="font-medium text-gray-900">({formatCurrency(profitLossData?.expenses?.totalGST)})</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300 font-semibold">
                      <span className="text-gray-900">Net GST Payable to ATO</span>
                      <span className="text-blue-600">{formatCurrency(profitLossData?.netGSTPayable)}</span>
                    </div>
                  </div>
                </div>

                {/* Net Income */}
                <div className="pt-4 border-t-4 border-gray-900">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-lg font-semibold text-gray-900">Net Income (before GST adjustment)</span>
                      <span className="text-lg font-semibold text-gray-900">{formatCurrency(profitLossData?.netIncomeBeforeGST)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700">Less: Net GST Payable</span>
                      <span className="font-medium text-red-600">({formatCurrency(profitLossData?.netGSTPayable)})</span>
                    </div>
                    <div className="flex justify-between items-center py-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg px-6 border-t-2 border-gray-900">
                      <span className="text-xl font-bold text-gray-900">Net Income (after GST)</span>
                      <span className="text-2xl font-bold text-green-600">{formatCurrency(profitLossData?.netIncome)}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-right text-sm text-gray-600">
                    Net Margin: {getPercentage(profitLossData?.netIncome, profitLossData?.revenue?.total)}%
                  </div>
                </div>
              </div>
            </div>)
          ) : (
            /* Balance Sheet */
            (<div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">Balance Sheet (AUD)</h2>
                  <span className="text-sm text-gray-600">
                    As of {new Date(selectedPeriod + '-01')?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-8">
                  {/* Assets */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      Assets
                    </h3>
                    <div className="space-y-4 ml-7">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Current Assets</h4>
                        <div className="space-y-1 ml-4 text-sm">
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Cash and Cash Equivalents</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.assets?.currentAssets?.cashUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.assets?.currentAssets?.cash)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Accounts Receivable</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.assets?.currentAssets?.accountsReceivableUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.assets?.currentAssets?.accountsReceivable)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">GST Receivable (Input Tax Credits)</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.assets?.currentAssets?.gstReceivableUSD)} USD)</span>
                            </div>
                            <span className="font-medium text-green-600">{formatCurrency(balanceSheetData?.assets?.currentAssets?.gstReceivable)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Inventory</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.assets?.currentAssets?.inventoryUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.assets?.currentAssets?.inventory)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-t border-gray-300 font-semibold">
                            <span className="text-gray-900">Total Current Assets</span>
                            <span>{formatCurrency(balanceSheetData?.assets?.currentAssets?.total)}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Fixed Assets</h4>
                        <div className="space-y-1 ml-4 text-sm">
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Property & Equipment</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.assets?.fixedAssets?.propertyEquipmentUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.assets?.fixedAssets?.propertyEquipment)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Accumulated Depreciation</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.assets?.fixedAssets?.accumulatedDepreciationUSD)} USD)</span>
                            </div>
                            <span className="font-medium text-red-600">{formatCurrency(balanceSheetData?.assets?.fixedAssets?.accumulatedDepreciation)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-t border-gray-300 font-semibold">
                            <span className="text-gray-900">Total Fixed Assets</span>
                            <span>{formatCurrency(balanceSheetData?.assets?.fixedAssets?.total)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-3 border-t-2 border-gray-900">
                        <div className="flex justify-between py-2 font-bold text-lg">
                          <span className="text-gray-900">Total Assets</span>
                          <span className="text-green-600">{formatCurrency(balanceSheetData?.assets?.totalAssets)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities & Equity */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      Liabilities & Equity
                    </h3>
                    <div className="space-y-4 ml-7">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Current Liabilities</h4>
                        <div className="space-y-1 ml-4 text-sm">
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Accounts Payable</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.liabilities?.currentLiabilities?.accountsPayableUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.liabilities?.currentLiabilities?.accountsPayable)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Accrued Expenses</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.liabilities?.currentLiabilities?.accruedExpensesUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.liabilities?.currentLiabilities?.accruedExpenses)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">GST Payable to ATO</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.liabilities?.currentLiabilities?.gstPayableUSD)} USD)</span>
                            </div>
                            <span className="font-medium text-red-600">{formatCurrency(balanceSheetData?.liabilities?.currentLiabilities?.gstPayable)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-t border-gray-300 font-semibold">
                            <span className="text-gray-900">Total Current Liabilities</span>
                            <span>{formatCurrency(balanceSheetData?.liabilities?.currentLiabilities?.total)}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Long-term Debt</h4>
                        <div className="ml-4 text-sm">
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Long-term Debt</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.liabilities?.longTermDebtUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.liabilities?.longTermDebt)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-t border-gray-300 font-semibold">
                            <span className="text-gray-900">Total Liabilities</span>
                            <span className="text-red-600">{formatCurrency(balanceSheetData?.liabilities?.totalLiabilities)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-3 border-t-2 border-gray-400">
                        <h4 className="font-medium text-gray-900 mb-2">Equity</h4>
                        <div className="space-y-1 ml-4 text-sm">
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Owner Equity</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.equity?.ownerEquityUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.equity?.ownerEquity)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <div>
                              <span className="text-gray-700">Retained Earnings</span>
                              <span className="text-xs text-gray-500 ml-2">({formatUSDCurrency(balanceSheetData?.equity?.retainedEarningsUSD)} USD)</span>
                            </div>
                            <span className="font-medium">{formatCurrency(balanceSheetData?.equity?.retainedEarnings)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-t border-gray-300 font-semibold">
                            <span className="text-gray-900">Total Equity</span>
                            <span className="text-blue-600">{formatCurrency(balanceSheetData?.equity?.totalEquity)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-3 border-t-2 border-gray-900">
                        <div className="flex justify-between py-2 font-bold text-lg">
                          <span className="text-gray-900">Total Liabilities & Equity</span>
                          <span className="text-blue-600">{formatCurrency(balanceSheetData?.liabilities?.totalLiabilities + balanceSheetData?.equity?.totalEquity)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>)
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialStatements;